/**
 * IUH Split Test Engine v3
 *
 * Deterministic multi-variant bucketing with Shopify-native revenue attribution.
 * Assignments are written into cart attributes so they land on the real order,
 * which is what makes per-variant revenue (not just clicks) measurable.
 */

(function () {
  'use strict';

  var VISITOR_COOKIE = 'iuh_vid';
  var VISITOR_TTL_DAYS = 180;
  var ATTR_PREFIX = '_ab_';

  var memoryStore = {};

  var Engine = {
    assignments: {},
    tests: [],

    // Rule 2: cookie-based state only — localStorage breaks inside Shopify iframes.
    getCookie: function (key) {
      try {
        var match = document.cookie.split(';').find(function (c) {
          return c.trim().indexOf(key + '=') === 0;
        });
        return match ? decodeURIComponent(match.split('=').slice(1).join('=')) : null;
      } catch (e) {
        return memoryStore[key] || null;
      }
    },

    setCookie: function (key, value, days) {
      try {
        var d = new Date();
        d.setTime(d.getTime() + days * 86400000);
        document.cookie =
          key + '=' + encodeURIComponent(value) +
          ';expires=' + d.toUTCString() + ';path=/;SameSite=Lax';
      } catch (e) {
        memoryStore[key] = value;
      }
    },

    getVisitorId: function () {
      var id = this.getCookie(VISITOR_COOKIE);
      if (!id) {
        id = 'v' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
        this.setCookie(VISITOR_COOKIE, id, VISITOR_TTL_DAYS);
      }
      return id;
    },

    // FNV-1a. Same visitor + same test always lands in the same bucket, so
    // assignment survives cookie-blocked sessions and needs no storage round-trip.
    hash: function (str) {
      var h = 2166136261;
      for (var i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
      }
      return (h >>> 0);
    },

    pickVariant: function (test, visitorId) {
      var variants = test.variants || [];
      if (!variants.length) return null;

      var total = variants.reduce(function (sum, v) {
        return sum + (typeof v.weight === 'number' ? v.weight : 0);
      }, 0);
      if (total <= 0) return null;

      var bucket = this.hash(visitorId + ':' + test.id) % 10000;
      var target = (bucket / 10000) * total;

      var cursor = 0;
      for (var i = 0; i < variants.length; i++) {
        cursor += variants[i].weight || 0;
        if (target < cursor) return variants[i];
      }
      return variants[variants.length - 1];
    },

    /**
     * Shopify Markets prefixes the path with a market handle (/en-ususd/products/x,
     * /nl-nleur/products/x). Most traffic lands on a prefixed URL, so matching the
     * raw path would fire the test only on the unprefixed market — shrinking the
     * sample and biasing it to one country.
     */
    normalizePath: function (raw) {
      var path = (raw || '/').replace(/\/+$/, '') || '/';
      // Market handles range from "en-us" to "en-australiaaud".
      path = path.replace(/^\/[a-z]{2}-[a-z]{2,20}(?=\/|$)/, '');
      return path || '/';
    },

    matchesPage: function (test) {
      var pages = test.pages;
      if (!pages || pages === 'all') return true;
      var path = this.normalizePath(window.location.pathname);
      return pages.some(function (p) {
        if (p === 'all') return true;
        var clean = p.replace(/\/+$/, '') || '/';
        return path === clean || path.indexOf(clean + '/') === 0;
      });
    },

    init: function () {
      this.tests = (window.__IUH_AB_TESTS__ || []).filter(function (t) {
        return t && t.status === 'running';
      });

      var visitorId = this.getVisitorId();
      var self = this;

      this.tests.forEach(function (test) {
        if (!self.matchesPage(test)) return;

        var variant = self.pickVariant(test, visitorId);
        if (!variant) return;

        self.assignments[test.id] = variant.id;
        self.applyVariant(test, variant);
        self.trackExposure(test, variant);
      });

      this.syncCartAttributes();
      this.watchCartAdds();
    },

    applyVariant: function (test, variant) {
      if (variant.css) {
        var style = document.createElement('style');
        style.setAttribute('data-ab-test', test.id);
        style.textContent = variant.css;
        document.head.appendChild(style);
      }

      // Variants declare real functions in the config file — no eval, no
      // stringified source to keep escaped and in sync.
      if (typeof variant.apply === 'function') {
        try {
          variant.apply({ test: test, variant: variant, engine: Engine });
        } catch (e) {
          console.error('[AB] variant apply failed', test.id, variant.id, e);
        }
      }

      document.documentElement.setAttribute('data-ab-' + test.id, variant.id);
    },

    trackExposure: function (test, variant) {
      this.pushDataLayer('ab_exposure', {
        ab_test_id: test.id,
        ab_variant_id: variant.id,
        ab_test_name: test.name || test.id
      });
      this.tagClarity(ATTR_PREFIX + test.id, variant.id);
    },

    trackConversion: function (name, data) {
      this.pushDataLayer('ab_conversion', Object.assign(
        { ab_conversion: name, ab_assignments: this.assignments },
        data || {}
      ));
    },

    pushDataLayer: function (event, payload) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: event }, payload));
    },

    // Clarity is lazy-loaded on first interaction, so it is usually absent at
    // exposure time. Queue tags and flush once the global appears.
    clarityQueue: [],
    clarityWatcher: null,

    tagClarity: function (key, value) {
      this.clarityQueue.push([key, value]);
      this.flushClarity();
    },

    flushClarity: function () {
      var self = this;
      if (typeof window.clarity !== 'function') {
        if (!this.clarityWatcher) {
          var attempts = 0;
          this.clarityWatcher = setInterval(function () {
            attempts++;
            if (typeof window.clarity === 'function') {
              clearInterval(self.clarityWatcher);
              self.clarityWatcher = null;
              self.flushClarity();
            } else if (attempts > 120) {
              clearInterval(self.clarityWatcher);
              self.clarityWatcher = null;
            }
          }, 1000);
        }
        return;
      }

      while (this.clarityQueue.length) {
        var pair = this.clarityQueue.shift();
        try {
          window.clarity('set', pair[0], pair[1]);
        } catch (e) {
          /* Clarity rejected the tag; nothing actionable client-side */
        }
      }
    },

    /**
     * Writes assignments onto the cart so they persist into the Shopify order
     * as note_attributes. Underscore-prefixed keys stay hidden from the customer
     * at checkout but remain readable via the Admin API.
     */
    syncCartAttributes: function () {
      var ids = Object.keys(this.assignments);
      if (!ids.length) return;

      var self = this;
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) {
          if (!cart || !cart.item_count) return;

          var existing = cart.attributes || {};
          var updates = {};
          ids.forEach(function (testId) {
            var key = ATTR_PREFIX + testId;
            if (existing[key] !== self.assignments[testId]) {
              updates[key] = self.assignments[testId];
            }
          });

          if (!Object.keys(updates).length) return;

          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ attributes: updates })
          });
        })
        .catch(function () { /* attribution is best-effort, never block the cart */ });
    },

    // A cart only exists after the first add, so re-sync whenever one happens.
    watchCartAdds: function () {
      var self = this;
      var origFetch = window.fetch;

      if (typeof origFetch === 'function') {
        window.fetch = function () {
          // Always invoke with window as receiver. Callers in strict-mode modules
          // (Shopify's own bundles, most apps) call bare fetch(), so `this` is
          // undefined there and native fetch throws "Illegal invocation".
          var promise = origFetch.apply(window, arguments);

          // Observe only. Never chain onto the returned promise — the caller must
          // get the original object back, and an added .then() would also swallow
          // rejections that belong to them.
          try {
            var input = arguments[0];
            var url = typeof input === 'string' ? input : (input && input.url) || '';
            if (url.indexOf('/cart/add') !== -1 && promise && promise.then) {
              promise.then(function (res) {
                if (res && res.ok) {
                  setTimeout(function () { self.syncCartAttributes(); }, 150);
                }
              })['catch'](function () { /* caller owns this failure */ });
            }
          } catch (e) { /* never let attribution break a cart request */ }

          return promise;
        };
      }

      document.addEventListener('submit', function (e) {
        if (e.target && e.target.querySelector('[name="add"]')) {
          self.trackConversion('add_to_cart');
          setTimeout(function () { self.syncCartAttributes(); }, 1200);
        }
      });

      document.addEventListener('click', function (e) {
        var t = e.target;
        if (t && (t.name === 'checkout' || (t.closest && t.closest('[name="checkout"]')))) {
          self.trackConversion('checkout_click');
        }
      });
    }
  };

  window.IUH_AB = Engine;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { Engine.init(); });
  } else {
    Engine.init();
  }
})();
